import React, { useRef, useState, useContext } from 'react'; // Add useContext
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { motion } from 'framer-motion';
import { AuthContext } from '../context/AuthContext'; // Import AuthContext

const HomePage = () => {
  // Access AuthContext to check if user is logged in
  const { user, loading } = useContext(AuthContext);

  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3,
      },
    },
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10,
      },
    },
  };

  const logoVariants = {
    hidden: { scale: 0.8, opacity: 0, y: -50, rotate: 10 },
    visible: {
      scale: 1,
      opacity: 1,
      y: 0,
      rotate: 0,
      transition: {
        type: 'spring',
        stiffness: 80,
        damping: 12,
        duration: 0.6,
      },
    },
    rotate3D: {
      rotateX: [0, 8, -8, 0],
      rotateY: [0, 12, -12, 0],
      transition: {
        repeat: Infinity,
        duration: 8,
        ease: 'easeInOut',
      },
    },
  };

  const titleVariants = {
    hidden: { scale: 0.5, opacity: 0, rotate: -10 },
    visible: {
      scale: 1,
      opacity: 1,
      rotate: 0,
      transition: {
        type: 'spring',
        stiffness: 120,
        damping: 15,
        duration: 0.8,
      },
    },
    glow: {
      textShadow: ['0 0 10px rgba(37, 150, 190, 0.5)', '0 0 20px rgba(37, 150, 190, 0.8)', '0 0 10px rgba(37, 150, 190, 0.5)'],
      transition: {
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut',
      },
    },
  };

  const buttonVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 10,
      },
    },
    hover: {
      scale: 1.1,
      backgroundColor: '#1c6e8c',
      transition: {
        duration: 0.3,
      },
    },
    tap: {
      scale: 0.95,
      transition: {
        duration: 0.2,
      },
    },
  };

  const sectionTitleVariants = {
    hidden: { scale: 0.8, opacity: 0, x: -30 },
    visible: {
      scale: 1,
      opacity: 1,
      x: 0,
      transition: {
        duration: 0.6,
        ease: 'easeOut',
      },
    },
    slide: {
      x: [-5, 5, -5],
      transition: {
        repeat: Infinity,
        duration: 3,
        ease: 'easeInOut',
      },
    },
  };

  const subheadlineVariants = {
    hidden: { width: 0, opacity: 0 },
    visible: {
      width: 'auto',
      opacity: 1,
      transition: {
        duration: 1.2,
        ease: 'easeInOut',
      },
    },
    underline: {
      boxShadow: '0 2px 0 rgba(37, 150, 190, 0.5)',
      transition: {
        repeat: Infinity,
        duration: 2,
        ease: 'easeInOut',
      },
    },
  };

  // Mouse interactivity for logo
  const logoRef = useRef(null);
  const [rotate, setRotate] = useState({ x: 0, y: 0 });

  const handleMouseMove = (e) => {
    if (!logoRef.current) return;
    const rect = logoRef.current.getBoundingClientRect();
    const centerX = rect.left + rect.width / 2;
    const centerY = rect.top + rect.height / 2;
    const mouseX = e.clientX - centerX;
    const mouseY = e.clientY - centerY;
    const maxTilt = 15;
    const rotateY = (mouseX / rect.width) * maxTilt;
    const rotateX = -(mouseY / rect.height) * maxTilt;
    setRotate({ x: rotateX, y: rotateY });
  };

  const handleMouseLeave = () => {
    setRotate({ x: 0, y: 0 });
  };

  // Don't render buttons while loading to avoid incorrect state
  if (loading) {
    return null; // Or a loading spinner
  }

  return (
    <div className="flex flex-col min-h-screen bg-white">
      <style>
        {`
          .logo-container {
            position: relative;
            perspective: 1000px;
          }
          .logo-u {
            position: relative;
            width: 200px;
            height: 200px;
            transition: transform 0.2s ease-out;
          }
          .logo-u::before {
            content: 'U';
            position: absolute;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            font-size: 120px;
            font-weight: bold;
            color: transparent;
            -webkit-text-stroke: 4px #2596be;
            text-shadow: 0 0 10px rgba(37, 150, 190, 0.5);
          }
          .logo-u img {
            width: 100%;
            height: 100%;
            object-fit: contain;
            filter: drop-shadow(0 0 10px rgba(37, 150, 190, 0.5));
          }
        `}
      </style>

      {/* Hero Section */}
      <motion.div
        className="min-h-[100vh] flex flex-col items-center justify-center bg-white"
        initial="hidden"
        animate="visible"
        variants={containerVariants}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <Header />

        {/* Logo with 3D Rotation and Mouse Interactivity */}
        <motion.div
          className="logo-container"
          ref={logoRef}
          style={{
            transform: `rotateX(${rotate.x}deg) rotateY(${rotate.y}deg)`,
          }}
        >
          <motion.img
            src="/logo.png"
            alt="The A.I. Union Logo"
            className="logo-u w-50 h-50 mb-6"
            variants={logoVariants}
            initial="hidden"
            animate={['visible', 'rotate3D']}
            style={{ transformStyle: 'preserve-3d' }}
          />
        </motion.div>

        {/* Title with Scale, Rotate */}
        <motion.h1
          className="text-5xl font-bold text-gray-900 tracking-wider mb-4"
          variants={titleVariants}
          initial="hidden"
          animate={['visible']}
        >
          The AI Union
        </motion.h1>

        {/* Tagline */}
        <motion.p
          className="text-2xl text-gray-900 mb-8 font-bold text-center"
          variants={itemVariants}
        >
          Unite Your Business with A.I.
        </motion.p>

        {/* Buttons with Join/Login Style */}
        <motion.div className="flex space-x-4" variants={containerVariants}>
          <motion.div variants={buttonVariants}>
            <Link to="/ai-insights">
              <motion.div
                className="px-5 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg text-white font-bold leading-6 text-center"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <span className="block text-lg font-bold">A.I. Insights</span>
                <span className="block text-sm font-bold">Get Started</span>
              </motion.div>
            </Link>
          </motion.div>

          <motion.div variants={buttonVariants}>
            <Link to={user ? "/intakeform" : "/login"}> {/* Conditional redirect */}
              <motion.div
                className="px-5 py-2 bg-gradient-to-r from-cyan-500 to-blue-600 rounded-lg text-white font-bold leading-6 text-center"
                variants={buttonVariants}
                whileHover="hover"
                whileTap="tap"
              >
                <span className="block text-lg font-bold">Get Custom Report</span>
                <span className="block text-sm font-bold">Get Started</span>
              </motion.div>
            </Link>
          </motion.div>
        </motion.div>
      </motion.div>

      {/* New Section (Outside Hero) */}
      <motion.section
        className="py-16 flex flex-col items-center"
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        variants={containerVariants}
      >
        <motion.h2
          className="text-3xl font-semibold text-[#2596be] mb-4 text-center"
          variants={sectionTitleVariants}
          animate={['visible', 'slide']}
        >
          The AI Union - Your All-in-One A.I. Solutions—On Demand, 24/7
        </motion.h2>
        <motion.p
          className="text-lg text-gray-700 text-center max-w-3xl px-4 overflow-hidden"
          variants={subheadlineVariants}
          animate={['visible']}
        >
          We help businesses run smarter, faster, and more efficiently with AI-powered insights,
          automation, and intelligent agents—without complexity.
        </motion.p>
      </motion.section>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default HomePage;